/**
 * "✨ Help me write" — one line, inside the bottom of a message card (Sofia, Oct 15 2026: one for
 * the InMail AND one for the follow-up). Enter rewrites the card's text in place through the same
 * `/refine` route and guards; "Undo" puts back the text from before the last rewrite.
 *
 * A plain `<input>` inside a `<label>`: the documented deviation of EmailBodyEditor's prompt (the DS
 * Input hides its placeholder until focus, which made the field look unusable).
 */
import { useState } from 'react';
import { logAnalyticsEvent } from '../../../../analytics/nativeAnalytics';
import * as api from '../../services/taOutreachApi';

export interface RewriteResult {
  subject: string;
  body: string;
  warnings: string[];
}

export function RewriteBar({
  candidateId,
  subject,
  body,
  onApply,
  disabled,
  placeholder,
  kind,
}: {
  candidateId: string;
  subject: string;
  body: string;
  /** The rewritten text, or the text from before the rewrite on Undo (warnings then empty). */
  onApply: (next: RewriteResult) => void;
  disabled?: boolean;
  placeholder: string;
  /** For the analytics event only. */
  kind: 'inmail' | 'follow_up';
}) {
  const [instruction, setInstruction] = useState('');
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState<string | null>(null);
  const [undo, setUndo] = useState<{ subject: string; body: string } | null>(null);

  async function rewrite() {
    const ask = instruction.trim();
    if (!ask || busy) return;
    setBusy(true);
    setNote(null);
    try {
      const r = await api.refineOutreach(candidateId, subject, body, ask);
      if (r.ok && r.subject && r.body) {
        setUndo({ subject, body });
        onApply({ subject: r.subject, body: r.body, warnings: r.warnings || [] });
        setInstruction('');
        void logAnalyticsEvent('ta_outreach_draft_refined', { outcome: 'applied', warning_count: (r.warnings || []).length, kind });
      } else {
        setNote(r.refusal || 'Not applied.');
        void logAnalyticsEvent('ta_outreach_draft_refined', { outcome: 'refused', warning_count: 0, kind });
      }
    } catch (err) {
      setNote(err instanceof Error ? err.message : 'The rewrite did not work just now.');
      void logAnalyticsEvent('ta_outreach_draft_refined', { outcome: 'failed', warning_count: 0, kind });
    } finally {
      setBusy(false);
    }
  }

  if (disabled) return null;
  return (
    <div className="border-t border-solid border-x-0 border-b-0 border-[#E9EAEC] px-3 py-2">
      <label className="flex items-center gap-2 rounded-full border border-solid border-[#E9EAEC] bg-[#F8F8F8] px-3 py-1.5 text-[13px] focus-within:border-[#0072EA] focus-within:bg-white">
        <span aria-hidden>✨</span>
        <input
          className="min-w-0 flex-1 border-0 bg-transparent p-0 outline-none placeholder:text-[#9B9B9B]"
          placeholder={placeholder}
          value={instruction}
          disabled={busy}
          onChange={(e) => setInstruction(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              void rewrite();
            }
          }}
        />
        {busy && <span className="shrink-0 text-xs text-[#707070]">Rewriting…</span>}
        {undo && !busy && (
          <button
            type="button"
            className="shrink-0 text-xs text-[#0072EA] underline"
            onClick={() => {
              onApply({ ...undo, warnings: [] });
              setUndo(null);
              void logAnalyticsEvent('ta_outreach_refine_undone', { kind });
            }}
          >
            Undo
          </button>
        )}
      </label>
      {note && <p className="mt-1.5 px-1 text-[13px] text-[#B3261E]">{note}</p>}
    </div>
  );
}
